
import React, { useState, useEffect, useRef } from 'react';
import { Check, Crown, CreditCard, AlertTriangle } from 'lucide-react';
import { db } from '../lib/firebase';
import { collection, addDoc, onSnapshot } from 'firebase/firestore';
import { User } from '../types';
import { APP_CONFIG } from '../config';
import { isAndroid } from '../utils/platformUtils';

interface SubscriptionModalProps {
  user: User;
  onShowTerms: () => void;
}

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ user, onShowTerms }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const communityEnabled = !isAndroid();

  const clearPendingWatchers = () => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearPendingWatchers();
    };
  }, []);

  const handlePayment = async () => {
    const stripePriceId = APP_CONFIG.STRIPE_PRICE_ID?.trim();
    const isValidPriceId = !!stripePriceId && /^price_[A-Za-z0-9]+$/.test(stripePriceId);
    if (!isValidPriceId) {
      setError("Configuration Error: A valid Stripe Price ID is required in src/config.ts");
      return;
    }

    setProcessing(true);
    setError(null);
    clearPendingWatchers();

    try {
      const checkoutSessionRef = await addDoc(collection(db, 'customers', user.uid, 'checkout_sessions'), {
        price: stripePriceId,
        success_url: window.location.origin + '?payment_success=true', // Added flag for App.tsx detection
        cancel_url: window.location.origin,
        mode: 'subscription',
        trial_period_days: 7, // Force 7-day free trial on checkout
        allow_promotion_codes: true,
      });

      unsubscribeRef.current = onSnapshot(checkoutSessionRef, (snap) => {
        const data = snap.data();
        if (data?.error) {
          const msg = data.error.message;
          if (msg.includes("No such price") && msg.includes("live mode key")) {
            setError("Setup Error: You are using a LIVE Stripe Key in Firebase, but this Price ID is from TEST Mode. Please create the product in Stripe Live Mode and update src/config.ts.");
          } else {
            setError(`Stripe Error: ${msg}`);
          }
          setProcessing(false);
        }
        if (data?.url) {
          let checkoutUrl: URL;
          try {
            checkoutUrl = new URL(data.url);
          } catch {
            setError("Unexpected checkout response. Please try again.");
            setProcessing(false);
            return;
          }

          const isStripeHost = checkoutUrl.hostname === 'checkout.stripe.com' || checkoutUrl.hostname.endsWith('.stripe.com');
          if (checkoutUrl.protocol !== 'https:' || !isStripeHost) {
            setError("Unexpected checkout destination blocked for safety.");
            setProcessing(false);
            return;
          }

          window.location.assign(data.url);
        }
      });

      timeoutRef.current = setTimeout(() => {
        setProcessing(prev => {
          if (prev) {
            setError("Connection Timeout: The subscription server is taking too long to respond. If you just deployed, please try again in 1 minute.");
            return false;
          }
          return prev;
        });
      }, 45000);

    } catch (err) {
      console.error("Subscription Error", err);
      setError("Network error. Please check your connection.");
      setProcessing(false);
    }
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-brand-950 p-6 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full bg-[url('https://images.unsplash.com/photo-1533577116850-9cc66cad8a9b?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')] bg-cover bg-center opacity-5 pointer-events-none"></div>
      <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/90 to-white pointer-events-none"></div>

      <div className="relative z-10 max-w-md w-full bg-brand-900/90 backdrop-blur-md border border-brand-800 p-8 rounded-2xl shadow-2xl text-center animate-fade-in">

        <div className="w-16 h-16 bg-gradient-to-br from-gold-400 to-gold-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-gold-500/20">
          <Crown className="text-white w-8 h-8" />
        </div>

        <h2 className="text-3xl font-serif font-bold text-white mb-2">Club Membership</h2>
        <p className="text-gray-300 mb-8">Unlock exclusive access to the world's premier digital cigar lounge.</p>

        {/* Free Trial Badge */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide mb-4 inline-block shadow-lg">
          🎉 7 Days Free Trial
        </div>

        <div className="flex items-baseline justify-center gap-1 mb-2">
          <span className="text-4xl font-bold text-gold-600">$4.99</span>
          <span className="text-brand-400 font-medium">/ month</span>
        </div>
        <p className="text-brand-400 text-sm mb-8">First 7 days free, then $4.99/month</p>

        <div className="space-y-4 mb-8 text-left">
          <div className="flex items-center gap-3">
            <div className="p-1 bg-green-100 rounded-full"><Check size={14} className="text-green-600" /></div>
            <span className="text-brand-200">Unlimited AI Sommelier Access</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-1 bg-green-100 rounded-full"><Check size={14} className="text-green-600" /></div>
            <span className="text-brand-200">Global Lounge Locator & Maps</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-1 bg-green-100 rounded-full"><Check size={14} className="text-green-600" /></div>
            <span className="text-brand-200">{communityEnabled ? 'Curated Weekly Community Picks' : 'Curated Weekly Tasting Picks'}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-1 bg-green-100 rounded-full"><Check size={14} className="text-green-600" /></div>
            <span className="text-brand-200">Digital Humidor Logbook</span>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-200 flex items-start gap-2 text-left">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <button
          onClick={handlePayment}
          disabled={processing}
          className="w-full py-4 px-6 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-400 hover:bg-brand-800 text-white font-bold text-lg rounded-xl shadow-lg transform transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:transform-none flex items-center justify-center gap-2"
        >
          {processing ? (
            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
          ) : (
            <>
              <CreditCard size={20} />
              Subscribe Now
            </>
          )}
        </button>

        <p className="mt-6 text-xs text-brand-400">
          7-day free trial. Secure payment processed by Stripe. Cancel anytime.
          <br />
          <button onClick={onShowTerms} className="underline hover:text-gold-500 mt-2 transition-colors">
            Read Terms of Service
          </button>
        </p>
      </div>
    </div>
  );
};
